import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import { useAuth } from "../auth/AuthContext";
import {
  listPortfolios,
  listPortfolioItems,
  createPortfolio,
  renamePortfolio,
  setPortfolioAlerts,
  deletePortfolio,
  removePortfolioItem,
  getCompany,
} from "../data/request";
import { getDateDisplay } from "../data/utilities";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY } from "./colors";
import { ProGate } from "./Components/ProGate";
import { CompanyStatus } from "./Components/CompanyStatus";
import { EnterprisePill } from "./Components/EnterprisePill";
import { DynamicPortfoliosPanel } from "./DynamicPortfoliosPanel";

type PortfolioTab = "portfolios" | "dynamic";

type Portfolio = { portfolio_id: string; name: string; alerts_enabled?: boolean };
type PortfolioItem = { company_number: string; company_name: string };
type CompanySummary = { company_status: string; date_of_creation: string };

export const PortfolioPage = () => {
  const auth = useAuth();
  const location = useLocation();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [items, setItems] = useState<Record<string, PortfolioItem[]>>({});
  const [companyDetails, setCompanyDetails] = useState<Record<string, CompanySummary>>({});
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newAlertsEnabled, setNewAlertsEnabled] = useState(true);
  const [error, setError] = useState<string>();
  const [portfolioToDelete, setPortfolioToDelete] = useState<Portfolio>();
  const [renamingId, setRenamingId] = useState<string>();
  const [renameValue, setRenameValue] = useState("");
  const [itemToRemove, setItemToRemove] = useState<{ portfolioId: string; companyNumber: string; companyName: string }>();
  const [portfolioAlertsToggle, setPortfolioAlertsToggle] = useState<Portfolio>();
  const [tab, setTab] = useState<PortfolioTab>("portfolios");
  const [highlightSearchId, setHighlightSearchId] = useState<string>();

  // Arriving from SearchResultPage.tsx's "view it" link on an already-saved
  // Dynamic Portfolio - it hands the target tab and search_id via router
  // state (not URL params, same reasoning DynamicPortfoliosPanel's "Run this
  // search again" link uses state instead of query params) so this lands
  // directly on the right tab with the right card scrolled into view, rather
  // than dumping the user on whatever tab happens to be default.
  useEffect(() => {
    const navState = location.state as { tab?: PortfolioTab; searchId?: string } | null;
    if (navState?.tab) {
      setTab(navState.tab);
    }
    if (navState?.searchId) {
      setHighlightSearchId(navState.searchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadAll = async () => {
    setLoading(true);
    const doc = await listPortfolios();
    const list: Portfolio[] = doc?.portfolios ?? [];
    setPortfolios(list);

    const itemsByPortfolio: Record<string, PortfolioItem[]> = {};
    await Promise.all(
      list.map(async (p) => {
        const itemsDoc = await listPortfolioItems(p.portfolio_id);
        itemsByPortfolio[p.portfolio_id] = itemsDoc?.items ?? [];
      })
    );
    setItems(itemsByPortfolio);
    setLoading(false);

    // Fetch live status/incorporation date for every distinct company across
    // all portfolios - a stored copy would go stale (e.g. once a company
    // dissolves), and this is exactly the kind of thing a watchlist exists
    // to catch.
    const uniqueNumbers = Array.from(new Set(Object.values(itemsByPortfolio).flat().map((i) => i.company_number)));
    const details: Record<string, CompanySummary> = {};
    await Promise.all(
      uniqueNumbers.map(async (number) => {
        const companyDoc = await getCompany(number);
        if (companyDoc) {
          details[number] = { company_status: companyDoc.company_status, date_of_creation: companyDoc.date_of_creation };
        }
      })
    );
    setCompanyDetails(details);
  };

  useEffect(() => {
    if (auth.isPro) {
      loadAll();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isPro]);

  if (!auth.isPro) {
    return <ProGate feature="My Portfolio" />;
  }

  const handleCreatePortfolio = async () => {
    setError(undefined);
    const name = newName.trim();
    if (!name) return;
    const doc = await createPortfolio(name, newAlertsEnabled);
    if (doc?.error) {
      setError(doc.error);
      return;
    }
    setNewName("");
    setNewAlertsEnabled(true);
    await loadAll();
  };

  const handleConfirmDeletePortfolio = async () => {
    if (!portfolioToDelete) return;
    await deletePortfolio(portfolioToDelete.portfolio_id);
    setPortfolioToDelete(undefined);
    await loadAll();
  };

  const handleConfirmRemoveItem = async () => {
    if (!itemToRemove) return;
    const { portfolioId, companyNumber } = itemToRemove;
    setItemToRemove(undefined);
    await removePortfolioItem(portfolioId, companyNumber);
    setItems({ ...items, [portfolioId]: (items[portfolioId] ?? []).filter((i) => i.company_number !== companyNumber) });
  };

  const handleStartRename = (p: Portfolio) => {
    setRenamingId(p.portfolio_id);
    setRenameValue(p.name);
  };

  const handleConfirmRename = async () => {
    const name = renameValue.trim();
    if (!name || !renamingId) {
      setRenamingId(undefined);
      return;
    }
    const doc = await renamePortfolio(renamingId, name);
    if (doc?.error) {
      setError(doc.error);
    } else {
      setPortfolios(portfolios.map((p) => (p.portfolio_id === renamingId ? { ...p, name } : p)));
    }
    setRenamingId(undefined);
  };

  const handleConfirmToggleAlerts = async () => {
    if (!portfolioAlertsToggle) return;
    const p = portfolioAlertsToggle;
    setPortfolioAlertsToggle(undefined);
    const nextValue = !(p.alerts_enabled ?? true);
    setPortfolios(portfolios.map((x) => (x.portfolio_id === p.portfolio_id ? { ...x, alerts_enabled: nextValue } : x)));
    const doc = await setPortfolioAlerts(p.portfolio_id, nextValue);
    if (doc?.error) {
      setError(doc.error);
      setPortfolios(portfolios.map((x) => (x.portfolio_id === p.portfolio_id ? { ...x, alerts_enabled: !nextValue } : x)));
    }
  };

  return (
    <Box>
      <Typography variant="h5" style={{ color: DELTA_PRIMARY, fontWeight: 700, marginBottom: 16 }}>
        {auth.isEnterprise ? "My Portfolios" : "Favourites"}
      </Typography>

      {/* Tab itself is always visible/clickable to any signed-in Pro+ user -
          gating happens on the tab's content below, mirroring how the
          Financials/Shareholders/Gazette Notices tabs work on the company
          details page (always-visible tab, gated content). */}
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        style={{ marginBottom: 16 }}
      >
        <Tab icon={<FolderIcon fontSize="small" />} iconPosition="start" label="Portfolios" value="portfolios" />
        <Tab icon={<DynamicFeedIcon fontSize="small" />} iconPosition="start" label="Dynamic Portfolios" value="dynamic" />
      </Tabs>

      {tab === "dynamic" &&
        (auth.isEnterprise ? (
          <DynamicPortfoliosPanel highlightSearchId={highlightSearchId} />
        ) : (
          <ProGate
            feature="Dynamic Portfolios"
            minTier="enterprise"
            description="Never miss a new competitor, prospect, or acquisition target. Save any Advanced Search and DeltaGain will email you the moment a new company matches it - no manual re-checking required."
          />
        ))}

      {tab === "portfolios" && loading && (
        <Box style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {tab === "portfolios" && !loading && (
        <>
      {portfolios.map((p) => (
        <Paper key={p.portfolio_id} elevation={0} style={{ padding: 20, marginBottom: 16 }}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {renamingId === p.portfolio_id ? (
              <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <TextField
                  size="small"
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleConfirmRename();
                    if (e.key === "Escape") setRenamingId(undefined);
                  }}
                />
                <IconButton size="small" onClick={handleConfirmRename} style={{ color: DELTA_ACCENT }}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setRenamingId(undefined)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700 }}>
                  {p.name}
                </Typography>
                {p.portfolio_id !== "default" && (
                  <IconButton size="small" onClick={() => handleStartRename(p)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
            {p.portfolio_id !== "default" && (
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Tooltip title={(p.alerts_enabled ?? true) ? "Email alerts on" : "Email alerts off"}>
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <Switch
                      size="small"
                      checked={p.alerts_enabled ?? true}
                      onChange={() => setPortfolioAlertsToggle(p)}
                    />
                    <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                      Email me of changes
                    </Typography>
                  </Box>
                </Tooltip>
                <Button size="small" onClick={() => setPortfolioToDelete(p)} style={{ color: "#C62828" }}>
                  Delete Portfolio
                </Button>
              </Box>
            )}
          </Box>

          {(items[p.portfolio_id] ?? []).length === 0 ? (
            <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginTop: 8 }}>
              No companies added yet.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Incorporated</TableCell>
                  <TableCell>Company Number</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(items[p.portfolio_id] ?? []).map((item) => {
                  const summary = companyDetails[item.company_number];
                  return (
                    <TableRow key={item.company_number}>
                      <TableCell>
                        <RouterLink to={`/company/${item.company_number}`} style={{ color: DELTA_ACCENT, textDecoration: "none", fontWeight: 600 }}>
                          {item.company_name || item.company_number}
                        </RouterLink>
                      </TableCell>
                      <TableCell>{summary && <CompanyStatus status={summary.company_status} />}</TableCell>
                      <TableCell>{summary ? getDateDisplay(summary.date_of_creation) : ""}</TableCell>
                      <TableCell>{item.company_number}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            setItemToRemove({
                              portfolioId: p.portfolio_id,
                              companyNumber: item.company_number,
                              companyName: item.company_name || item.company_number,
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      ))}

      <Paper elevation={0} style={{ padding: 20 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700 }}>
            Create a New Portfolio
          </Typography>
          {!auth.isEnterprise && <EnterprisePill />}
        </Box>
        {error && (
          <Typography variant="body2" style={{ color: "#C62828", marginBottom: 8 }}>
            {error}
          </Typography>
        )}
        <Tooltip title={!auth.isEnterprise ? "Upgrade to Enterprise to create additional portfolios" : ""}>
          <Box style={{ display: "flex", gap: 8 }}>
            <TextField
              label="Portfolio Name"
              size="small"
              value={newName}
              disabled={!auth.isEnterprise}
              onChange={(e) => setNewName(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button variant="contained" disabled={!auth.isEnterprise} onClick={handleCreatePortfolio} style={{ backgroundColor: DELTA_ACCENT }}>
              Create
            </Button>
          </Box>
        </Tooltip>

        {auth.isEnterprise && (
          <Box style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Switch
              size="small"
              checked={newAlertsEnabled}
              onChange={(e) => setNewAlertsEnabled(e.target.checked)}
            />
            <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
              Email me of changes
            </Typography>
          </Box>
        )}

        {/* Preview for Pro users who can't create a named portfolio yet -
            alerts are on by default for any portfolio you do create, so this
            hints at that opt-in-by-default behaviour too, not just that the
            feature exists. Enterprise users already see the real toggle once
            they create a portfolio, so this preview would be redundant for them. */}
        {!auth.isEnterprise && (
          <Tooltip title="Included free with every Enterprise portfolio">
            <Box style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
              <Switch size="small" checked disabled />
              <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                Email me of changes
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Paper>
        </>
      )}

      <Dialog open={Boolean(portfolioToDelete)} onClose={() => setPortfolioToDelete(undefined)}>
        <DialogTitle>Delete "{portfolioToDelete?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the portfolio and every company in it
            {(items[portfolioToDelete?.portfolio_id ?? ""] ?? []).length > 0 &&
              ` (${items[portfolioToDelete?.portfolio_id ?? ""]?.length} companies)`}
            . This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioToDelete(undefined)}>Cancel</Button>
          <Button onClick={handleConfirmDeletePortfolio} style={{ color: "#C62828" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(itemToRemove)} onClose={() => setItemToRemove(undefined)}>
        <DialogTitle>Remove "{itemToRemove?.companyName}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>This removes the company from this portfolio. This can't be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemToRemove(undefined)}>Cancel</Button>
          <Button onClick={handleConfirmRemoveItem} style={{ color: "#C62828" }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(portfolioAlertsToggle)} onClose={() => setPortfolioAlertsToggle(undefined)}>
        <DialogTitle>
          {(portfolioAlertsToggle?.alerts_enabled ?? true) ? "Turn off email alerts?" : "Turn on email alerts?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(portfolioAlertsToggle?.alerts_enabled ?? true)
              ? `You will stop receiving emails when companies in "${portfolioAlertsToggle?.name}" change.`
              : `You will start receiving emails when companies in "${portfolioAlertsToggle?.name}" change.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioAlertsToggle(undefined)}>Cancel</Button>
          <Button onClick={handleConfirmToggleAlerts} style={{ color: DELTA_ACCENT }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioPage;
